import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Editor } from "@/components/help/Editor";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

type HelpCategory = "documentation" | "faq" | "tutorial";

const EditContent = () => {
  const [title, setTitle] = useState("");
  const [content, setContent] = useState(""); // This will store the initial content for the form
  const [category, setCategory] = useState<HelpCategory>("documentation");
  const [editorContent, setEditorContent] = useState(""); // This will store the content as edited
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();

  useEffect(() => {
    fetchContent();
  }, [id]);

  const fetchContent = async () => {
    const { data, error } = await supabase
      .from('help_content')
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      console.error('Error fetching content:', error);
      return;
    }

    if (data) {
      setTitle(data.title);
      setContent(data.content); // initial content value for form
      setEditorContent(data.content); // initial content for the editor
      setCategory(data.category);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted");

    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to edit content",
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase
      .from('help_content')
      .update({
        title,
        content: editorContent, // Use editorContent for final content
        category,
        updated_by: user.id,
      })
      .eq('id', id);

    if (error) {
      toast({
        title: "Error",
        description: "Failed to update content",
        variant: "destructive",
      });
      return;
    }

    toast({
      title: "Success",
      description: "Content updated successfully",
    });
    navigate(`/dashboard/help/${category}`);
  };

  // Wrap the editor section with an event handler that prevents closing when interacting
  const handleEditorClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent click from propagating upwards
  };

  return (
    <div className="space-y-6" onClick={handleEditorClick}>
      <div className="flex justify-between items-center">
        <h1 className="text-4xl font-bold text-primary">Edit Content</h1>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Title</label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Category</label>
          <Select value={category} onValueChange={(value: HelpCategory) => setCategory(value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="documentation">Documentation</SelectItem>
              <SelectItem value="faq">FAQ</SelectItem>
              <SelectItem value="tutorial">Tutorial</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-2">
          <label className="text-sm font-medium">Content</label>
          {/* Pass the editorContent state to Editor and handle updates */}
          <Editor content={editorContent} onChange={setEditorContent} editable={true} />
        </div>

        <div className="flex justify-end gap-4">
          <Button variant="outline" onClick={() => navigate(-1)}>
            Cancel
          </Button>
          <Button type="submit">Update</Button>
        </div>
      </form>
    </div>
  );
};


export default EditContent;
